import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, TablePagination, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { adminFetch } from '../../lib/adminAuth';

// What each FCM rejection actually means, so a failed test send points at the
// thing to go and fix rather than just reporting that it failed.
const PUSH_ERROR_HELP = {
  'messaging/third-party-auth-error':
    'Apple rejected the credential Firebase presented. Usual causes, in order: (1) the iOS app registered in Firebase has a different bundle ID than the build on the device, (2) the app is registered in a different Firebase project than the server\'s service account, (3) no APNs Auth Key (.p8) uploaded. Check that the shipped GoogleService-Info.plist has the same BUNDLE_ID as the build AND the same PROJECT_ID as the server key.',
  'messaging/mismatched-credential':
    'The device token was minted by a different Firebase project than the server is sending from. Either the app build and the server key are from different projects, or this token predates a project migration — in which case it is dead until the user installs the new build and reopens the app.',
  'messaging/registration-token-not-registered':
    'The device token is no longer valid — usually the app was uninstalled, or the build was signed for a different APNs environment (sandbox vs production). The token has been removed; reopen the app to register a fresh one.',
  'messaging/invalid-registration-token':
    'The stored device token is malformed. It has been removed; reopen the app to register a fresh one.',
  'messaging/invalid-argument':
    'FCM rejected the message payload or token. The token has been removed; reopen the app to register a fresh one.',
  'messaging/authentication-error':
    'The server\'s Firebase service account was rejected — the key may have been revoked.',
  'push/not-configured':
    'The server has no Firebase credentials loaded, so push is disabled entirely.',
};

// Flags come from the ISO code as regional indicator characters — same trick
// the mobile app uses, so there are no assets to keep in sync.
const flagOf = (code) => String(code || '')
  .toUpperCase()
  .replace(/[^A-Z]/g, '')
  .replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 15;

  // Send-a-notification dialog. `target` is the customer being messaged.
  const [target, setTarget] = useState(null);
  const [title, setTitle] = useState('Test notification');
  const [body, setBody] = useState('This is a test from the Rewarding admin panel.');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const openSend = (customer) => {
    setTarget(customer);
    setResult(null);
  };

  const closeSend = () => {
    if (sending) return;
    setTarget(null);
    setResult(null);
  };

  const send = async () => {
    if (!target || !title.trim() || !body.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const res = await adminFetch('/api/admin/notifications/send', {
        method: 'POST',
        body: JSON.stringify({
          target: 'customer',
          customerId: target._id,
          title: title.trim(),
          body: body.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Send failed');
      setResult({ ok: true, ...data });
    } catch (err) {
      setResult({ ok: false, message: err.message });
    } finally {
      setSending(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`/api/admin/customers?page=${page + 1}&limit=${limit}&search=${encodeURIComponent(search)}`);
      const data = await res.json();
      setCustomers(data.customers || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      await adminFetch(`/api/admin/customers/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>Customers</Typography>
      <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>Manage all registered customers</Typography>

      <TextField
        placeholder="Search by name, email or mobile..."
        value={search}
        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        size="small"
        sx={{ mb: 2, width: 320 }}
      />

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #f0f0f0', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Country</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Postcode</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Gender</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>DOB</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Verified</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Joined</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : customers.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: '#999' }}>No customers found</TableCell></TableRow>
            ) : (
              customers.map((c) => (
                <TableRow key={c._id} hover>
                  <TableCell sx={{ fontSize: 13 }}>{c.full_name}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{c.email || (c.mobile ? `+${c.mobile}` : '-')}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {c.country ? `${flagOf(c.country)} ${c.country}` : '-'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{c.postcode || '-'}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{c.gender || '-'}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{formatDate(c.dob)}</TableCell>
                  <TableCell>
                    <Chip label={c.verified ? 'Yes' : 'No'} size="small"
                      sx={{ fontSize: 11, bgcolor: c.verified ? '#e8f5e9' : '#fff3e0', color: c.verified ? '#2e7d32' : '#e65100' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={c.status || 'active'} size="small"
                      sx={{ fontSize: 11, bgcolor: c.status === 'suspended' ? '#ffebee' : '#e8f5e9', color: c.status === 'suspended' ? '#c62828' : '#2e7d32' }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{formatDate(c.createdAt)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openSend(c)} title="Send notification">
                      <NotificationsActiveIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => toggleStatus(c._id, c.status || 'active')}
                      title={c.status === 'suspended' ? 'Activate' : 'Suspend'}>
                      {c.status === 'suspended'
                        ? <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                        : <BlockIcon sx={{ fontSize: 18, color: '#999' }} />
                      }
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          rowsPerPageOptions={[limit]}
          sx={{ borderTop: '1px solid #f0f0f0' }}
        />
      </TableContainer>

      {/* Send a one-off notification to a single customer. Useful as an
          end-to-end check that push delivery is actually working. */}
      <Dialog open={!!target} onClose={closeSend} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          Send notification
        </DialogTitle>
        <DialogContent>
          {target && (
            <>
              <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: '#fafafa', border: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>
                  {target.full_name || '(no name)'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#777' }}>
                  {target.email || (target.mobile ? `+${target.mobile}` : '—')}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#999', mt: 0.5 }}>
                  {(target.pushTokens || []).length} registered device
                  {(target.pushTokens || []).length === 1 ? '' : 's'}
                </Typography>
              </Box>

              {/* Without a device token there is nothing to push to — the in-app
                  notification still lands, so say exactly what will happen. */}
              {(target.pushTokens || []).length === 0 && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  This customer has no registered device. The notification will
                  appear in the app's notification list, but no push will be delivered.
                </Alert>
              )}

              {target.status === 'suspended' && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  This customer is suspended. The notification will still be sent.
                </Alert>
              )}

              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              />
              <TextField
                label="Message"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                fullWidth
                size="small"
                multiline
                rows={3}
              />

              {result && (
                <Alert
                  severity={result.ok ? (result.sent > 0 ? 'success' : 'warning') : 'error'}
                  sx={{ mt: 2, borderRadius: 2 }}
                >
                  {!result.ok
                    ? result.message
                    : result.sent > 0
                      ? `Delivered to ${result.sent} device${result.sent === 1 ? '' : 's'}.`
                      : (
                        <>
                          <Box sx={{ fontWeight: 600, mb: (result.errors || []).length ? 0.75 : 0 }}>
                            {`Saved to the in-app list, but no push was delivered${
                              result.failed ? ` (${result.failed} device(s) failed)` : ''
                            }.`}
                          </Box>
                          {(result.errors || []).map((code) => (
                            <Box key={code} sx={{ mt: 0.75 }}>
                              <Box component="code" sx={{ fontSize: 12, bgcolor: 'rgba(0,0,0,0.06)', px: 0.6, py: 0.2, borderRadius: 0.5 }}>
                                {code}
                              </Box>
                              <Box sx={{ fontSize: 13, mt: 0.5 }}>
                                {PUSH_ERROR_HELP[code] || 'Unrecognised FCM error — check the server logs for details.'}
                              </Box>
                            </Box>
                          ))}
                        </>
                      )}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeSend} disabled={sending} sx={{ textTransform: 'none', color: '#777' }}>
            Close
          </Button>
          <Button
            onClick={send}
            disabled={sending || !title.trim() || !body.trim()}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
